/**
 * useFollowUp.ts — Issue #333.
 *
 * Admin-side hooks for follow-up plans, visits, questionnaire responses,
 * outcome incidents, and follow-up reports.
 *
 * 红线：
 *   - 只使用 generated @jinzhuan/api-client；不手写 fetch。
 *   - 所有 mutation 必须带 expectedVersion 走乐观锁。
 *   - 错误通过 ApiError 抛回 UI；不静默吞掉 HTTP 状态。
 *   - 不在前端伪造 approved/published 状态；mutation 必须用后端返回值刷新状态。
 *   - 列表视图只展示 compact summary 字段，详情视图才取整张 content / answersJson。
 */
import { useCallback, useState } from 'react';
import {
  createApiClient,
  ApiError as ApiClientError,
} from '@/lib/adminApiClient';
import type { ApiComponents } from '@/lib/adminApiClient';
import { getStoredAccessToken } from '@/lib/tokenStorage';

const apiClient = createApiClient({
  baseUrl: '',
  getAccessToken: () => getStoredAccessToken(),
});

// ============================================================================
// Type aliases
// ============================================================================

export type FollowUpPlanSummaryDto =
  ApiComponents['schemas']['FollowUpPlanSummaryDto'];
export type FollowUpPlanDto = ApiComponents['schemas']['FollowUpPlanDto'];
export type FollowUpPlanListResponseDto =
  ApiComponents['schemas']['FollowUpPlanListResponseDto'];
export type CreateFollowUpPlanDto =
  ApiComponents['schemas']['CreateFollowUpPlanDto'];
export type UpdateFollowUpPlanDto =
  ApiComponents['schemas']['UpdateFollowUpPlanDto'];
export type FollowUpPlanTransitionDto =
  ApiComponents['schemas']['FollowUpPlanTransitionDto'];
export type CancelFollowUpPlanDto =
  ApiComponents['schemas']['CancelFollowUpPlanDto'];

export type FollowUpVisitSummaryDto =
  ApiComponents['schemas']['FollowUpVisitSummaryDto'];
export type FollowUpVisitDto = ApiComponents['schemas']['FollowUpVisitDto'];
export type FollowUpVisitListResponseDto =
  ApiComponents['schemas']['FollowUpVisitListResponseDto'];
export type CreateFollowUpVisitDto =
  ApiComponents['schemas']['CreateFollowUpVisitDto'];
export type UpdateFollowUpVisitDto =
  ApiComponents['schemas']['UpdateFollowUpVisitDto'];
export type FollowUpVisitVersionDto =
  ApiComponents['schemas']['FollowUpVisitVersionDto'];
export type CompleteFollowUpVisitDto =
  ApiComponents['schemas']['CompleteFollowUpVisitDto'];
export type MissedFollowUpVisitDto =
  ApiComponents['schemas']['MissedFollowUpVisitDto'];
export type CancelFollowUpVisitDto =
  ApiComponents['schemas']['CancelFollowUpVisitDto'];

export type FollowUpQuestionnaireResponseSummaryDto =
  ApiComponents['schemas']['FollowUpQuestionnaireResponseSummaryDto'];
export type FollowUpQuestionnaireResponseDto =
  ApiComponents['schemas']['FollowUpQuestionnaireResponseDto'];
export type CreateFollowUpQuestionnaireResponseDto =
  ApiComponents['schemas']['CreateFollowUpQuestionnaireResponseDto'];
export type UpdateFollowUpQuestionnaireResponseDto =
  ApiComponents['schemas']['UpdateFollowUpQuestionnaireResponseDto'];

export type OutcomeIncidentSummaryDto =
  ApiComponents['schemas']['OutcomeIncidentSummaryDto'];
export type OutcomeIncidentDto =
  ApiComponents['schemas']['OutcomeIncidentDto'];
export type OutcomeIncidentListResponseDto =
  ApiComponents['schemas']['OutcomeIncidentListResponseDto'];
export type CreateOutcomeIncidentDto =
  ApiComponents['schemas']['CreateOutcomeIncidentDto'];
export type UpdateOutcomeIncidentDto =
  ApiComponents['schemas']['UpdateOutcomeIncidentDto'];
export type OutcomeIncidentVersionDto =
  ApiComponents['schemas']['OutcomeIncidentVersionDto'];
export type OutcomeIncidentPlanActionDto =
  ApiComponents['schemas']['OutcomeIncidentPlanActionDto'];
export type CloseOutcomeIncidentDto =
  ApiComponents['schemas']['CloseOutcomeIncidentDto'];

export type FollowUpReportSummaryDto =
  ApiComponents['schemas']['FollowUpReportSummaryDto'];
export type FollowUpReportDto = ApiComponents['schemas']['FollowUpReportDto'];
export type FollowUpReportListResponseDto =
  ApiComponents['schemas']['FollowUpReportListResponseDto'];
export type CreateFollowUpReportDto =
  ApiComponents['schemas']['CreateFollowUpReportDto'];
export type UpdateFollowUpReportDto =
  ApiComponents['schemas']['UpdateFollowUpReportDto'];
export type FollowUpReportTransitionDto =
  ApiComponents['schemas']['FollowUpReportTransitionDto'];
export type RequestChangesFollowUpReportDto =
  ApiComponents['schemas']['RequestChangesFollowUpReportDto'];
export type FollowUpReportReviewDto =
  ApiComponents['schemas']['FollowUpReportReviewDto'];

export type FollowUpPlanStatus = FollowUpPlanSummaryDto['status'];
export type FollowUpVisitStatus = FollowUpVisitSummaryDto['status'];
export type FollowUpVisitKind = FollowUpVisitSummaryDto['visitKind'];
export type FollowUpVisitChannel = FollowUpVisitSummaryDto['channel'];
export type OutcomeIncidentStatus = OutcomeIncidentSummaryDto['status'];
export type OutcomeIncidentType = OutcomeIncidentSummaryDto['incidentType'];
export type OutcomeIncidentSeverity = OutcomeIncidentSummaryDto['severity'];
export type FollowUpReportStatus = FollowUpReportSummaryDto['status'];

export const FOLLOW_UP_PLAN_STATUS_LABELS: Record<FollowUpPlanStatus, string> = {
  active: '执行中',
  paused: '暂停',
  completed: '已完成',
  cancelled: '已取消',
};

export const FOLLOW_UP_VISIT_STATUS_LABELS: Record<
  FollowUpVisitStatus,
  string
> = {
  scheduled: '已排定',
  in_progress: '进行中',
  completed: '已完成',
  missed: '未应答',
  cancelled: '已取消',
};

export const FOLLOW_UP_VISIT_KIND_LABELS: Record<FollowUpVisitKind, string> = {
  general: '常规随访',
  post_infusion: '回输后随访',
  adverse_event: '不良反应跟踪',
  satisfaction: '满意度回访',
  other: '其他',
};

export const FOLLOW_UP_VISIT_CHANNEL_LABELS: Record<
  FollowUpVisitChannel,
  string
> = {
  phone: '电话',
  onsite: '现场',
  wechat: '微信',
  video: '视频',
  other: '其他',
};

export const OUTCOME_INCIDENT_STATUS_LABELS: Record<
  OutcomeIncidentStatus,
  string
> = {
  open: '待跟进',
  investigating: '调查中',
  action_planned: '已制定行动',
  resolved: '已解决',
  closed_without_action: '关闭未处理',
};

export const OUTCOME_INCIDENT_TYPE_LABELS: Record<
  OutcomeIncidentType,
  string
> = {
  no_felt_improvement: '无明显改善',
  indicator_unchanged: '指标未改善',
  dissatisfaction: '客户不满',
  abnormal_reaction: '异常反应',
  dispute_risk: '纠纷风险',
  other: '其他',
};

export const OUTCOME_INCIDENT_SEVERITY_LABELS: Record<
  OutcomeIncidentSeverity,
  string
> = {
  low: '低',
  normal: '一般',
  high: '高',
  critical: '紧急',
};

export const FOLLOW_UP_REPORT_STATUS_LABELS: Record<
  FollowUpReportStatus,
  string
> = {
  draft: '草稿',
  submitted: '审核中',
  changes_requested: '待修改',
  approved: '已审核',
  published: '已发布',
  archived: '已归档',
};

// ============================================================================
// Error helpers
// ============================================================================

export interface ApiError {
  message: string;
  code?: string;
  status?: number;
}

function toApiError(err: unknown, httpStatus?: number): ApiError {
  const status =
    httpStatus ?? (err instanceof ApiClientError ? err.status : undefined);
  if (status === 401) {
    return {
      message: '会话已失效，请重新登录后再试',
      code: 'UNAUTHENTICATED',
      status,
    };
  }
  if (status === 403) {
    return {
      message: '当前角色没有随访 / 疗效事件模块的访问权限',
      code: 'FORBIDDEN',
      status,
    };
  }
  if (status === 404) {
    return { message: '记录不存在或已被删除', code: 'NOT_FOUND', status };
  }
  if (status === 409) {
    return {
      message: '记录已被其他操作修改或状态不允许该操作，请刷新后重试',
      code: 'CONFLICT',
      status,
    };
  }
  if (status === 400) {
    return {
      message: '请求被拒绝，请检查必填字段与字段长度',
      code: 'BAD_REQUEST',
      status,
    };
  }
  if (err instanceof Error) {
    return { message: err.message, status };
  }
  return { message: String(err), status };
}

// ============================================================================
// Plans
// ============================================================================

export interface PlanListFilter {
  customerId?: string;
  serviceUserId?: string;
  orderId?: string;
  ownerUserId?: string;
  status?: FollowUpPlanStatus;
  search?: string;
  page?: number;
  limit?: number;
}

export function useFollowUpPlanList() {
  const [rows, setRows] = useState<FollowUpPlanSummaryDto[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(20);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  const fetchPlans = useCallback(async (filter?: PlanListFilter) => {
    setLoading(true);
    setError(null);
    try {
      const query: Record<string, string | number> = {};
      if (filter?.customerId) query.customerId = filter.customerId;
      if (filter?.serviceUserId) query.serviceUserId = filter.serviceUserId;
      if (filter?.orderId) query.orderId = filter.orderId;
      if (filter?.ownerUserId) query.ownerUserId = filter.ownerUserId;
      if (filter?.status) query.status = filter.status;
      if (filter?.search) query.search = filter.search;
      if (filter?.page) query.page = filter.page;
      if (filter?.limit) query.limit = filter.limit;

      const {
        data,
        error: apiErr,
        response,
      } = await apiClient.GET('/api/v1/follow-up/plans', { params: { query } });
      if (apiErr) {
        setError(toApiError(apiErr, (response as Response)?.status));
        return;
      }
      if (data) {
        setRows([...data.rows]);
        setTotal(data.total);
        setPage(data.page);
        setLimit(data.limit);
      }
    } catch (e) {
      setError(toApiError(e));
    } finally {
      setLoading(false);
    }
  }, []);

  return { rows, total, page, limit, loading, error, fetchPlans };
}

export function useFollowUpPlanDetail() {
  const [plan, setPlan] = useState<FollowUpPlanDto | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  const fetchPlan = useCallback(async (id: string) => {
    setLoading(true);
    setError(null);
    try {
      const { data, error: apiErr, response } = await apiClient.GET(
        '/api/v1/follow-up/plans/{id}',
        { params: { path: { id } } },
      );
      if (apiErr) {
        setError(toApiError(apiErr, (response as Response)?.status));
        return;
      }
      setPlan(data ?? null);
    } catch (e) {
      setError(toApiError(e));
    } finally {
      setLoading(false);
    }
  }, []);

  return { plan, loading, error, fetchPlan, setPlan };
}

export function useCreateFollowUpPlan() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  const createPlan = useCallback(
    async (dto: CreateFollowUpPlanDto): Promise<FollowUpPlanDto | null> => {
      setLoading(true);
      setError(null);
      try {
        const { data, error: apiErr, response } = await apiClient.POST(
          '/api/v1/follow-up/plans',
          { body: dto },
        );
        if (apiErr) {
          setError(toApiError(apiErr, (response as Response)?.status));
          return null;
        }
        return data ?? null;
      } catch (e) {
        setError(toApiError(e));
        return null;
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  return { createPlan, loading, error };
}

export function useFollowUpPlanTransitions() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  const update = useCallback(
    async (
      id: string,
      dto: UpdateFollowUpPlanDto,
    ): Promise<FollowUpPlanDto | null> => {
      setLoading(true);
      setError(null);
      try {
        const { data, error: apiErr, response } = await apiClient.PUT(
          '/api/v1/follow-up/plans/{id}',
          { params: { path: { id } }, body: dto },
        );
        if (apiErr) {
          setError(toApiError(apiErr, (response as Response)?.status));
          return null;
        }
        return data ?? null;
      } catch (e) {
        setError(toApiError(e));
        return null;
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  const transition = useCallback(
    async (
      action: 'pause' | 'resume' | 'complete',
      id: string,
      dto: FollowUpPlanTransitionDto,
    ): Promise<FollowUpPlanDto | null> => {
      setLoading(true);
      setError(null);
      try {
        const path = `/api/v1/follow-up/plans/{id}/${action}` as const;
        const { data, error: apiErr, response } = await apiClient.POST(path, {
          params: { path: { id } },
          body: dto,
        });
        if (apiErr) {
          setError(toApiError(apiErr, (response as Response)?.status));
          return null;
        }
        return data ?? null;
      } catch (e) {
        setError(toApiError(e));
        return null;
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  const cancel = useCallback(
    async (
      id: string,
      dto: CancelFollowUpPlanDto,
    ): Promise<FollowUpPlanDto | null> => {
      setLoading(true);
      setError(null);
      try {
        const { data, error: apiErr, response } = await apiClient.POST(
          '/api/v1/follow-up/plans/{id}/cancel',
          { params: { path: { id } }, body: dto },
        );
        if (apiErr) {
          setError(toApiError(apiErr, (response as Response)?.status));
          return null;
        }
        return data ?? null;
      } catch (e) {
        setError(toApiError(e));
        return null;
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  return { update, transition, cancel, loading, error };
}

// ============================================================================
// Visits
// ============================================================================

export interface VisitListFilter {
  planId?: string;
  status?: FollowUpVisitStatus;
  assigneeUserId?: string;
  page?: number;
  limit?: number;
}

export function useFollowUpVisitList() {
  const [rows, setRows] = useState<FollowUpVisitSummaryDto[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(20);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  const fetchVisits = useCallback(async (filter?: VisitListFilter) => {
    setLoading(true);
    setError(null);
    try {
      const query: Record<string, string | number> = {};
      if (filter?.planId) query.planId = filter.planId;
      if (filter?.status) query.status = filter.status;
      if (filter?.assigneeUserId) query.assigneeUserId = filter.assigneeUserId;
      if (filter?.page) query.page = filter.page;
      if (filter?.limit) query.limit = filter.limit;

      const {
        data,
        error: apiErr,
        response,
      } = await apiClient.GET('/api/v1/follow-up/visits', { params: { query } });
      if (apiErr) {
        setError(toApiError(apiErr, (response as Response)?.status));
        return;
      }
      if (data) {
        setRows([...data.rows]);
        setTotal(data.total);
        setPage(data.page);
        setLimit(data.limit);
      }
    } catch (e) {
      setError(toApiError(e));
    } finally {
      setLoading(false);
    }
  }, []);

  return { rows, total, page, limit, loading, error, fetchVisits };
}

export function useCreateFollowUpVisit() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  const createVisit = useCallback(
    async (dto: CreateFollowUpVisitDto): Promise<FollowUpVisitDto | null> => {
      setLoading(true);
      setError(null);
      try {
        const { data, error: apiErr, response } = await apiClient.POST(
          '/api/v1/follow-up/visits',
          { body: dto },
        );
        if (apiErr) {
          setError(toApiError(apiErr, (response as Response)?.status));
          return null;
        }
        return data ?? null;
      } catch (e) {
        setError(toApiError(e));
        return null;
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  return { createVisit, loading, error };
}

export function useFollowUpVisitTransitions() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  const start = useCallback(
    async (
      id: string,
      dto: FollowUpVisitVersionDto,
    ): Promise<FollowUpVisitDto | null> => {
      setLoading(true);
      setError(null);
      try {
        const { data, error: apiErr, response } = await apiClient.POST(
          '/api/v1/follow-up/visits/{id}/start',
          { params: { path: { id } }, body: dto },
        );
        if (apiErr) {
          setError(toApiError(apiErr, (response as Response)?.status));
          return null;
        }
        return data ?? null;
      } catch (e) {
        setError(toApiError(e));
        return null;
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  const complete = useCallback(
    async (
      id: string,
      dto: CompleteFollowUpVisitDto,
    ): Promise<FollowUpVisitDto | null> => {
      setLoading(true);
      setError(null);
      try {
        const { data, error: apiErr, response } = await apiClient.POST(
          '/api/v1/follow-up/visits/{id}/complete',
          { params: { path: { id } }, body: dto },
        );
        if (apiErr) {
          setError(toApiError(apiErr, (response as Response)?.status));
          return null;
        }
        return data ?? null;
      } catch (e) {
        setError(toApiError(e));
        return null;
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  const markMissed = useCallback(
    async (
      id: string,
      dto: MissedFollowUpVisitDto,
    ): Promise<FollowUpVisitDto | null> => {
      setLoading(true);
      setError(null);
      try {
        const { data, error: apiErr, response } = await apiClient.POST(
          '/api/v1/follow-up/visits/{id}/missed',
          { params: { path: { id } }, body: dto },
        );
        if (apiErr) {
          setError(toApiError(apiErr, (response as Response)?.status));
          return null;
        }
        return data ?? null;
      } catch (e) {
        setError(toApiError(e));
        return null;
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  const cancel = useCallback(
    async (
      id: string,
      dto: CancelFollowUpVisitDto,
    ): Promise<FollowUpVisitDto | null> => {
      setLoading(true);
      setError(null);
      try {
        const { data, error: apiErr, response } = await apiClient.POST(
          '/api/v1/follow-up/visits/{id}/cancel',
          { params: { path: { id } }, body: dto },
        );
        if (apiErr) {
          setError(toApiError(apiErr, (response as Response)?.status));
          return null;
        }
        return data ?? null;
      } catch (e) {
        setError(toApiError(e));
        return null;
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  return { start, complete, markMissed, cancel, loading, error };
}

// ============================================================================
// Outcome incidents
// ============================================================================

export interface IncidentListFilter {
  customerId?: string;
  planId?: string;
  status?: OutcomeIncidentStatus;
  severity?: OutcomeIncidentSeverity;
  incidentType?: OutcomeIncidentType;
  search?: string;
  page?: number;
  limit?: number;
}

export function useOutcomeIncidentList() {
  const [rows, setRows] = useState<OutcomeIncidentSummaryDto[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(20);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  const fetchIncidents = useCallback(async (filter?: IncidentListFilter) => {
    setLoading(true);
    setError(null);
    try {
      const query: Record<string, string | number> = {};
      if (filter?.customerId) query.customerId = filter.customerId;
      if (filter?.planId) query.planId = filter.planId;
      if (filter?.status) query.status = filter.status;
      if (filter?.severity) query.severity = filter.severity;
      if (filter?.incidentType) query.incidentType = filter.incidentType;
      if (filter?.search) query.search = filter.search;
      if (filter?.page) query.page = filter.page;
      if (filter?.limit) query.limit = filter.limit;
      const {
        data,
        error: apiErr,
        response,
      } = await apiClient.GET('/api/v1/follow-up/outcome-incidents', {
        params: { query },
      });
      if (apiErr) {
        setError(toApiError(apiErr, (response as Response)?.status));
        return;
      }
      if (data) {
        setRows([...data.rows]);
        setTotal(data.total);
        setPage(data.page);
        setLimit(data.limit);
      }
    } catch (e) {
      setError(toApiError(e));
    } finally {
      setLoading(false);
    }
  }, []);

  return { rows, total, page, limit, loading, error, fetchIncidents };
}

export function useCreateOutcomeIncident() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  const createIncident = useCallback(
    async (
      dto: CreateOutcomeIncidentDto,
    ): Promise<OutcomeIncidentDto | null> => {
      setLoading(true);
      setError(null);
      try {
        const { data, error: apiErr, response } = await apiClient.POST(
          '/api/v1/follow-up/outcome-incidents',
          { body: dto },
        );
        if (apiErr) {
          setError(toApiError(apiErr, (response as Response)?.status));
          return null;
        }
        return data ?? null;
      } catch (e) {
        setError(toApiError(e));
        return null;
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  return { createIncident, loading, error };
}

export function useOutcomeIncidentTransitions() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  const investigate = useCallback(
    async (
      id: string,
      dto: OutcomeIncidentVersionDto,
    ): Promise<OutcomeIncidentDto | null> => {
      setLoading(true);
      setError(null);
      try {
        const { data, error: apiErr, response } = await apiClient.POST(
          '/api/v1/follow-up/outcome-incidents/{id}/investigate',
          { params: { path: { id } }, body: dto },
        );
        if (apiErr) {
          setError(toApiError(apiErr, (response as Response)?.status));
          return null;
        }
        return data ?? null;
      } catch (e) {
        setError(toApiError(e));
        return null;
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  const planAction = useCallback(
    async (
      id: string,
      dto: OutcomeIncidentPlanActionDto,
    ): Promise<OutcomeIncidentDto | null> => {
      setLoading(true);
      setError(null);
      try {
        const { data, error: apiErr, response } = await apiClient.POST(
          '/api/v1/follow-up/outcome-incidents/{id}/plan-action',
          { params: { path: { id } }, body: dto },
        );
        if (apiErr) {
          setError(toApiError(apiErr, (response as Response)?.status));
          return null;
        }
        return data ?? null;
      } catch (e) {
        setError(toApiError(e));
        return null;
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  const close = useCallback(
    async (
      id: string,
      dto: CloseOutcomeIncidentDto,
    ): Promise<OutcomeIncidentDto | null> => {
      setLoading(true);
      setError(null);
      try {
        const { data, error: apiErr, response } = await apiClient.POST(
          '/api/v1/follow-up/outcome-incidents/{id}/close',
          { params: { path: { id } }, body: dto },
        );
        if (apiErr) {
          setError(toApiError(apiErr, (response as Response)?.status));
          return null;
        }
        return data ?? null;
      } catch (e) {
        setError(toApiError(e));
        return null;
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  return { investigate, planAction, close, loading, error };
}

// ============================================================================
// Reports
// ============================================================================

export interface ReportListFilter {
  customerId?: string;
  planId?: string;
  status?: FollowUpReportStatus;
  search?: string;
  page?: number;
  limit?: number;
}

export function useFollowUpReportList() {
  const [rows, setRows] = useState<FollowUpReportSummaryDto[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(20);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  const fetchReports = useCallback(async (filter?: ReportListFilter) => {
    setLoading(true);
    setError(null);
    try {
      const query: Record<string, string | number> = {};
      if (filter?.customerId) query.customerId = filter.customerId;
      if (filter?.planId) query.planId = filter.planId;
      if (filter?.status) query.status = filter.status;
      if (filter?.search) query.search = filter.search;
      if (filter?.page) query.page = filter.page;
      if (filter?.limit) query.limit = filter.limit;
      const {
        data,
        error: apiErr,
        response,
      } = await apiClient.GET('/api/v1/follow-up/reports', { params: { query } });
      if (apiErr) {
        setError(toApiError(apiErr, (response as Response)?.status));
        return;
      }
      if (data) {
        setRows([...data.rows]);
        setTotal(data.total);
        setPage(data.page);
        setLimit(data.limit);
      }
    } catch (e) {
      setError(toApiError(e));
    } finally {
      setLoading(false);
    }
  }, []);

  return { rows, total, page, limit, loading, error, fetchReports };
}

export function useFollowUpReportDetail() {
  const [report, setReport] = useState<FollowUpReportDto | null>(null);
  const [reviews, setReviews] = useState<FollowUpReportReviewDto[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  const fetchReport = useCallback(async (id: string) => {
    setLoading(true);
    setError(null);
    try {
      const {
        data: detail,
        error: detailErr,
        response: detailResp,
      } = await apiClient.GET('/api/v1/follow-up/reports/{id}', {
        params: { path: { id } },
      });
      if (detailErr) {
        setError(toApiError(detailErr, (detailResp as Response)?.status));
        return;
      }
      setReport(detail ?? null);
      const {
        data: reviewData,
        error: reviewErr,
        response: reviewResp,
      } = await apiClient.GET('/api/v1/follow-up/reports/{id}/reviews', {
        params: { path: { id } },
      });
      if (reviewErr) {
        setError(toApiError(reviewErr, (reviewResp as Response)?.status));
        return;
      }
      setReviews([...(reviewData ?? [])]);
    } catch (e) {
      setError(toApiError(e));
    } finally {
      setLoading(false);
    }
  }, []);

  return { report, reviews, loading, error, fetchReport, setReport };
}

export function useCreateFollowUpReport() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  const createReport = useCallback(
    async (dto: CreateFollowUpReportDto): Promise<FollowUpReportDto | null> => {
      setLoading(true);
      setError(null);
      try {
        const { data, error: apiErr, response } = await apiClient.POST(
          '/api/v1/follow-up/reports',
          { body: dto },
        );
        if (apiErr) {
          setError(toApiError(apiErr, (response as Response)?.status));
          return null;
        }
        return data ?? null;
      } catch (e) {
        setError(toApiError(e));
        return null;
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  return { createReport, loading, error };
}

export function useFollowUpReportTransitions() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  const submit = useCallback(
    async (
      id: string,
      dto: FollowUpReportTransitionDto,
    ): Promise<FollowUpReportDto | null> => {
      setLoading(true);
      setError(null);
      try {
        const { data, error: apiErr, response } = await apiClient.POST(
          '/api/v1/follow-up/reports/{id}/submit',
          { params: { path: { id } }, body: dto },
        );
        if (apiErr) {
          setError(toApiError(apiErr, (response as Response)?.status));
          return null;
        }
        return data ?? null;
      } catch (e) {
        setError(toApiError(e));
        return null;
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  const approve = useCallback(
    async (
      id: string,
      dto: FollowUpReportTransitionDto,
    ): Promise<FollowUpReportDto | null> => {
      setLoading(true);
      setError(null);
      try {
        const { data, error: apiErr, response } = await apiClient.POST(
          '/api/v1/follow-up/reports/{id}/approve',
          { params: { path: { id } }, body: dto },
        );
        if (apiErr) {
          setError(toApiError(apiErr, (response as Response)?.status));
          return null;
        }
        return data ?? null;
      } catch (e) {
        setError(toApiError(e));
        return null;
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  const requestChanges = useCallback(
    async (
      id: string,
      dto: RequestChangesFollowUpReportDto,
    ): Promise<FollowUpReportDto | null> => {
      setLoading(true);
      setError(null);
      try {
        const { data, error: apiErr, response } = await apiClient.POST(
          '/api/v1/follow-up/reports/{id}/request-changes',
          { params: { path: { id } }, body: dto },
        );
        if (apiErr) {
          setError(toApiError(apiErr, (response as Response)?.status));
          return null;
        }
        return data ?? null;
      } catch (e) {
        setError(toApiError(e));
        return null;
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  const publish = useCallback(
    async (
      id: string,
      dto: FollowUpReportTransitionDto,
    ): Promise<FollowUpReportDto | null> => {
      setLoading(true);
      setError(null);
      try {
        const { data, error: apiErr, response } = await apiClient.POST(
          '/api/v1/follow-up/reports/{id}/publish',
          { params: { path: { id } }, body: dto },
        );
        if (apiErr) {
          setError(toApiError(apiErr, (response as Response)?.status));
          return null;
        }
        return data ?? null;
      } catch (e) {
        setError(toApiError(e));
        return null;
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  const archive = useCallback(
    async (
      id: string,
      dto: FollowUpReportTransitionDto,
    ): Promise<FollowUpReportDto | null> => {
      setLoading(true);
      setError(null);
      try {
        const { data, error: apiErr, response } = await apiClient.POST(
          '/api/v1/follow-up/reports/{id}/archive',
          { params: { path: { id } }, body: dto },
        );
        if (apiErr) {
          setError(toApiError(apiErr, (response as Response)?.status));
          return null;
        }
        return data ?? null;
      } catch (e) {
        setError(toApiError(e));
        return null;
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  return { submit, approve, requestChanges, publish, archive, loading, error };
}
